import React, { useEffect, useState } from "react";
import { ShieldAlert, CalendarClock, Check } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, AYLAR } from "@/lib/api";

export const ADMIN_ALERT_TYPES = ["export_formula_warning", "prim_month_changed", "access_denied_repeat"];

const ayAd = (ym) => {
  const [y, m] = (ym || "").split("-");
  return `${AYLAR.find((a) => a.v === Number(m))?.n || m} ${y}`;
};

const CONFIG = {
  export_formula_warning: {
    title: "Güvenlik Uyarıları", Icon: ShieldAlert, tone: "rose", testid: "security-alerts",
    desc: "Dışa aktarılan dosyalarda formül gibi başlayan (=, +, -, @) hücreler bulundu. Excel'de düz metin olarak kaydedildi; CSV dosyasını açarken dikkatli olun.",
    head: (n) => `${n.meta?.export} · ${n.meta?.count} hücre`,
    detail: (n) => (n.meta?.cells || []).slice(0, 3).join(" · "),
  },
  prim_month_changed: {
    title: "Prim Ayı Değişiklikleri", Icon: CalendarClock, tone: "indigo", testid: "prim-month-alerts",
    desc: "Onay bekleyen primlerin ait olduğu ay personel veya muhasebeci tarafından değiştirildi.",
    head: (n) => `${n.meta?.ad || "—"} · ${n.meta?.icra_foy || ""}`,
    sub: (n) => n.meta?.by_name ? `Değiştiren: ${n.meta.by_name}` : (n.from_email || "—"),
    detail: (n) => `${ayAd(n.meta?.old)} → ${ayAd(n.meta?.new)}${n.meta?.muvekkil ? ` · ${n.meta.muvekkil}` : ""}${n.meta?.dosya_adi ? ` · ${n.meta.dosya_adi}` : ""}`,
  },
  access_denied_repeat: {
    title: "Tekrarlayan Yetkisiz Erişim", Icon: ShieldAlert, tone: "rose", testid: "access-denied-alerts",
    desc: "Bu kullanıcılar aynı gün içinde 5 veya daha fazla kez yetkileri olmayan sayfa/verilere erişmeye çalıştı.",
    head: (n) => n.meta?.name || n.from_email || "—",
    detail: (n) => `${fmtDate(n.meta?.day)} · ${n.meta?.count}+ deneme · son: ${n.meta?.last_path || ""}`,
  },
};

const TONES = {
  rose: { box: "bg-rose-50 border-rose-300", h: "text-rose-800", pill: "bg-rose-600", txt: "text-rose-700", item: "border-rose-200" },
  indigo: { box: "bg-indigo-50 border-indigo-300", h: "text-indigo-800", pill: "bg-indigo-600", txt: "text-indigo-700", item: "border-indigo-200" },
};

export function AdminAlertPanel({ type, onChanged }) {
  const cfg = CONFIG[type];
  const t = TONES[cfg.tone];
  const [rows, setRows] = useState([]);

  const load = () => api.get(`/admin-notifications?unread_only=true&entity_type=${type}&limit=100`)
    .then((r) => setRows(r.data?.rows || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.post(`/admin-notifications/${id}/mark-read`)));
      await load();
      onChanged?.();
    } catch { toast.error("İşaretlenemedi"); }
  };

  if (!rows.length) return null;
  return (
    <div className={`${t.box} border rounded-xl p-4 md:p-5 shadow-sm`} data-testid={`${cfg.testid}-panel`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className={`font-display text-base md:text-lg font-bold ${t.h} flex items-center gap-2`}>
          <cfg.Icon size={18} /> {cfg.title}
          <span className={`text-xs font-bold ${t.pill} text-white rounded-full px-2 py-0.5`} data-testid={`${cfg.testid}-count`}>{rows.length}</span>
        </h2>
        <button type="button" onClick={() => markRead(rows.map((r) => r.id))} data-testid={`${cfg.testid}-read-all-btn`}
          className={`text-xs font-semibold ${t.h} underline`}>Tümünü okundu say</button>
      </div>
      <p className={`text-xs ${t.txt} mb-3`}>{cfg.desc}</p>
      <div className="space-y-2">
        {rows.map((n) => (
          <div key={n.id} className={`bg-white border ${t.item} rounded-lg p-3 flex items-start gap-3`} data-testid={`${cfg.testid}-item-${n.id}`}>
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold text-slate-900">{cfg.head(n)}</div>
              <div className="text-xs text-slate-500 mt-0.5">{cfg.sub ? cfg.sub(n) : (n.from_email || "—")} · {fmtDate(n.created_at?.slice(0, 10))} {n.created_at?.slice(11, 16)}</div>
              <div className={`text-[11px] font-mono ${t.txt} mt-1 break-all`}>{cfg.detail(n)}</div>
            </div>
            <button type="button" onClick={() => markRead([n.id])} data-testid={`${cfg.testid}-read-${n.id}`}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-bold bg-slate-900 hover:bg-slate-700 text-white px-2.5 py-1.5 rounded-lg">
              <Check size={12} /> Okundu
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SecurityAlertsPanel({ onChanged }) {
  return (
    <>
      {ADMIN_ALERT_TYPES.map((t) => <AdminAlertPanel key={t} type={t} onChanged={onChanged} />)}
    </>
  );
}
